/* eslint-disable max-lines */
/* eslint-disable max-len */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { IJourneyReservationPassengerInfo } from "../../../interfaces/endpoints/trips/TripChangeNotification";

interface AffectedReservationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (selectedReservationIds: string[]) => void;
  affectedReservations: IJourneyReservationPassengerInfo[];
  tripId: number;
  contextText?: string;
}

const AffectedReservationsModal: React.FC<AffectedReservationsModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  affectedReservations,
  tripId,
  contextText
}) => {
  const [selectedReservations, setSelectedReservations] = useState<Set<string>>(new Set());
  const [selectedDates, setSelectedDates] = useState<Set<string>>(new Set());
  const didInit = useRef(false);

  // Helpers to parse YYYY-MM-DD consistently and format in Albanian
  const parseYmdToUtcDate = (ymd: string) => {
    const [y, m, d] = ymd.split("-").map((x) => parseInt(x, 10));
    if (!y || !m || !d) return new Date(ymd);
    return new Date(Date.UTC(y, m - 1, d));
  };

  const longDateFormatter = useMemo(
    () => new Intl.DateTimeFormat("sq", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: "UTC"
    }),
    []
  );

  const shortDateFormatter = useMemo(
    () => new Intl.DateTimeFormat("sq", {
      weekday: "short",
      month: "short",
      day: "numeric",
      timeZone: "UTC"
    }),
    []
  );

  const reservationsByDate = affectedReservations.reduce((acc, reservation) => {
    const date = reservation.journeyDate || "Datë e panjohur";
    if (!acc[date]) acc[date] = [] as IJourneyReservationPassengerInfo[];
    acc[date].push(reservation);
    return acc;
  }, {} as Record<string, IJourneyReservationPassengerInfo[]>);

  useEffect(() => {
    if (isOpen && !didInit.current && affectedReservations.length > 0) {
      const allReservationIds = new Set(affectedReservations.map((r) => r.reservationId));
      const allDates = new Set(Object.keys(reservationsByDate));
      setSelectedReservations(allReservationIds);
      setSelectedDates(allDates);
      didInit.current = true;
    }
  }, [isOpen, affectedReservations]);

  useEffect(() => {
    if (!isOpen) {
      didInit.current = false;
      setSelectedReservations(new Set());
      setSelectedDates(new Set());
    }
  }, [isOpen]);

  const handleDateToggle = (date: string, checked: boolean) => {
    const newSelectedDates = new Set(selectedDates);
    const newSelectedReservations = new Set(selectedReservations);
    if (checked) {
      newSelectedDates.add(date);
      reservationsByDate[date].forEach((reservation) =>
        newSelectedReservations.add(reservation.reservationId));
    } else {
      newSelectedDates.delete(date);
      reservationsByDate[date].forEach((reservation) =>
        newSelectedReservations.delete(reservation.reservationId));
    }
    setSelectedDates(newSelectedDates);
    setSelectedReservations(newSelectedReservations);
  };

  const handleReservationToggle = (reservationId: string, checked: boolean) => {
    const next = new Set(selectedReservations);
    if (checked) next.add(reservationId);
    else next.delete(reservationId);
    setSelectedReservations(next);
  };

  const handleSelectAll = () => {
    const allReservationIds = new Set(affectedReservations.map((r) => r.reservationId));
    const allDates = new Set(Object.keys(reservationsByDate));
    setSelectedReservations(allReservationIds);
    setSelectedDates(allDates);
  };

  const handleDeselectAll = () => {
    setSelectedReservations(new Set());
    setSelectedDates(new Set());
  };

  const handleConfirm = () => {
    onConfirm(Array.from(selectedReservations));
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start
    justify-center z-50 p-2 sm:p-4 pt-16">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[85vh] flex flex-col shadow-xl">
        <div className="flex justify-between items-center p-4 sm:p-6 border-b
        border-gray-200 bg-white rounded-t-lg">
          <h2 className="text-lg sm:text-xl font-semibold">
            Njofto pasagjerët e prekur ({affectedReservations.length} rezervime)
          </h2>
          <button
            onClick={onClose}
            className="px-3 py-2 text-sm text-gray-700 border border-gray-300 rounded-md
            hover:bg-gray-50 transition-colors flex items-center gap-2"
            aria-label="Vazhdo pa njoftuar"
          >
            <span>Vazhdo pa njoftuar</span>
            <span aria-hidden>×</span>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="mb-4 text-sm text-gray-600">
            <p>
              {contextText
                ? contextText
                : tripId > 0
                  ? `Udhëtimi ${tripId} është përditësuar. Zgjidhni cilat rezervime duhet
                  të marrin njoftime me email:`
                  : "Zgjidhni cilat rezervime duhet të marrin njoftime me email:"}
            </p>
            {affectedReservations.length > 0 && (
              <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-blue-800 font-medium">Përmbledhje:</p>
                <ul className="text-blue-700 text-xs mt-1 space-y-1">
                  <li>• Total rezervime të prekura: {affectedReservations.length}</li>
                  <li>• Datat e prekura: {Object.keys(reservationsByDate).length}</li>
                  <li>• Të gjitha rezervimet janë zgjedhur si parazgjedhje</li>
                  <li>• Përdorni kutitë e datës për të zgjedhur/çzgjedhur të gjitha rezervimet për një datë specifike</li>
                  <li>• Përdorni kutitë individuale për përzgjedhje të hollësishme</li>
                </ul>
              </div>
            )}
          </div>

          <div
            className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4
            space-y-3 sm:space-y-0"
          >
            <div className="flex flex-wrap gap-2">
              <button
                onClick={handleSelectAll}
                className="px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200
                transition-colors"
              >
                Zgjidh të gjitha
              </button>
              <button
                onClick={handleDeselectAll}
                className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200
                transition-colors"
              >
                Çzgjedh të gjitha
              </button>
            </div>
            <div className="text-sm text-gray-600 text-center sm:text-right">
              {selectedReservations.size} nga {affectedReservations.length} rezervime të zgjedhura
            </div>
          </div>

          <div className="space-y-4">
            {Object.entries(reservationsByDate).map(([date, reservations]) => (
              <div key={date} className="border rounded-lg p-3 sm:p-4">
                <div className="flex items-center mb-3">
                  <input
                    type="checkbox"
                    id={`date-${date}`}
                    checked={selectedDates.has(date)}
                    onChange={(e) => handleDateToggle(date, e.target.checked)}
                    className="mr-3 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                  />
                  <label
                    htmlFor={`date-${date}`}
                    className="font-medium text-base sm:text-lg flex-1"
                  >
                    {date === "Datë e panjohur" ? (
                      "Datë e panjohur"
                    ) : (
                      <span>
                        <span className="hidden sm:inline">
                          {longDateFormatter.format(parseYmdToUtcDate(date))}
                        </span>
                        <span className="sm:hidden">
                          {shortDateFormatter.format(parseYmdToUtcDate(date))}
                        </span>
                      </span>
                    )}
                  </label>
                  <span className="text-sm text-gray-500 ml-2">
                    ({reservations.length} rezervim{reservations.length !== 1 ? "e" : ""})
                  </span>
                </div>

                <div className="ml-6 sm:ml-8 space-y-2">
                  {reservations.map((reservation) => (
                    <div key={reservation.reservationId} className="flex items-center">
                      <input
                        type="checkbox"
                        id={`reservation-${reservation.reservationId}`}
                        checked={selectedReservations.has(reservation.reservationId)}
                        onChange={(e) => handleReservationToggle(reservation.reservationId, e.target.checked)}
                        className="mr-3 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      />
                      <label
                        htmlFor={`reservation-${reservation.reservationId}`}
                        className="text-sm flex-1"
                      >
                        <span className="font-medium break-all">{reservation.email}</span>
                        {reservation.hasActiveConcierge && (
                          <span className="ml-2 text-green-600 text-xs">✓ Konciergj</span>
                        )}
                        <span className="ml-2 text-gray-500 break-all">
                          ({reservation.reservationId})
                        </span>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-gray-200 p-4 sm:p-6 bg-gray-50 rounded-b-lg">
          <div
            className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-3 sm:space-y-0"
          >
            <div className="text-sm text-gray-600" />
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <button
                onClick={handleConfirm}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark
                transition-colors flex items-center justify-center space-x-2"
              >
                <span>Njofto rezervimet e zgjedhura</span>
                <span className="bg-primary-dark px-2 py-1 rounded text-xs">
                  {selectedReservations.size}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AffectedReservationsModal;
